/**
 * Shared kitchen-sink "parts" — cutout profiles + dual-shape dimensions.
 *
 * The kitchen layout pipeline (`materializeCandidate.js`) and the kitchen
 * post-processor (`functions/features/post-processing/kitchen.js`) both
 * create procedural kitchen-sink objects. They both have to populate
 *
 *   - `sink-cutout`         — points consumed by the COUNTERTOP CSG
 *                             (`Countertop.jsx`), which applies a 1"
 *                             radial inset internally.
 *   - `sink-cabinet-cutout` — points consumed by the CABINET CSG
 *                             (`ProceduralObject.jsx`), which uses the
 *                             points raw (no inset).
 *   - `dimensions`          — dual shape ({width,depth,height,cutoutDepth}
 *                             AND {x,y,z}) since two consumer layers read
 *                             different keys; omitting either silently
 *                             breaks rendering or collisions.
 *
 * Keep the two call sites lockstep by reading from this single module.
 *
 * Cutout `points` are in INCHES; the renderer applies INCHES_TO_METERS.
 * The basin dimensions passed in are in METERS.
 */

const {
  INCHES_TO_METERS,
  KITCHEN_SINK_BASIN,
  KITCHEN_DOUBLE_SINK_BASIN,
} = require('./constants');
const { buildSinkDimensions } = require('../sink-types');
const { SINK_BASE_SIDE_ALLOWANCE_IN } = require('../cabinet-runs/bays');

/**
 * The placeholder basin for a sink base `baseWidth` metres wide: the standard
 * `KITCHEN_SINK_BASIN` — or, for a sink of two bowls (`basinCount: 2`), the
 * standard `KITCHEN_DOUBLE_SINK_BASIN` — narrowed where it would not leave
 * the base `SINK_BASE_SIDE_ALLOWANCE_IN`, the 1½" a side a basin needs to
 * drop into a base at all. A 24" base takes a 21" single; 30" and wider keep
 * the 27". A double keeps its 32" from a 35" base up.
 */
function placeholderBasinFor(baseWidth, { basinCount = 1 } = {}) {
  const standard =
    basinCount === 2 ? KITCHEN_DOUBLE_SINK_BASIN : KITCHEN_SINK_BASIN;
  const widest = baseWidth - SINK_BASE_SIDE_ALLOWANCE_IN * INCHES_TO_METERS;
  return widest < standard.width ? { ...standard, width: widest } : standard;
}

/**
 * Cutout removed from the COUNTERTOP. We hand back the basin's half-extents
 * directly (in inches); `Countertop.jsx` insets by 1" radially before
 * subtracting, so the final hole ends up ~2" smaller than the basin in
 * each dimension. The countertop overhang then hides the basin's top
 * edge from above — undermount-style.
 */
function buildKitchenSinkCutout(basinDims = KITCHEN_SINK_BASIN) {
  const halfW = basinDims.width / INCHES_TO_METERS / 2;
  const halfD = basinDims.depth / INCHES_TO_METERS / 2;
  return {
    points: [
      { x: -halfW, z: -halfD },
      { x: halfW, z: -halfD },
      { x: halfW, z: halfD },
      { x: -halfW, z: halfD },
    ],
    metadata: {
      bounds: { minX: -halfW, maxX: halfW, minZ: -halfD, maxZ: halfD },
      // Relative to the sink origin (not extraction-frame world coords):
      // Countertop.jsx must NOT re-centre it on its bbox.
      originRelative: true,
    },
  };
}

/**
 * Cutout removed from the CABINET BODY. We pre-expand by 1" because the
 * cabinet CSG path uses points raw (no inset). The result is a hole 2"
 * bigger than the basin in each dimension so the basin walls clear the
 * cabinet body without z-fighting.
 */
const CABINET_OVERSIZE_INCHES = 1;
function buildKitchenSinkCabinetCutout(basinDims = KITCHEN_SINK_BASIN) {
  const halfW =
    basinDims.width / INCHES_TO_METERS / 2 + CABINET_OVERSIZE_INCHES;
  const halfD =
    basinDims.depth / INCHES_TO_METERS / 2 + CABINET_OVERSIZE_INCHES;
  return {
    points: [
      { x: -halfW, z: -halfD },
      { x: halfW, z: -halfD },
      { x: halfW, z: halfD },
      { x: -halfW, z: halfD },
    ],
    metadata: {
      bounds: { minX: -halfW, maxX: halfW, minZ: -halfD, maxZ: halfD },
      // Relative to the sink origin (not extraction-frame world coords):
      // Countertop.jsx must NOT re-centre it on its bbox.
      originRelative: true,
    },
  };
}

/**
 * Dual-shape dimensions block. Two consumer paths read different keys:
 *   {width, depth, height, cutoutDepth} — drawSinkOnCountertop in
 *     elementDrawingUtils.js (canvas sink draw pass).
 *   {x, y, z}                           — getObjectCorners /
 *     getObjectClearanceCorners in shared-lib/collision.
 * Skipping either set silently: bad rendering or NaN collisions.
 */
function buildKitchenSinkDimensions(basinDims = KITCHEN_SINK_BASIN) {
  return buildSinkDimensions(basinDims);
}

/**
 * Farmhouse (apron-front) cutouts. The apron stands proud of the cabinet
 * face, so both the countertop and the cabinet body must be open at the
 * FRONT, not just holed from above: the profile runs FRONT_OPEN_INCHES past
 * the sink's +Z face. After the countertop's 1" radial inset the hole still
 * clears the counter's front edge; the cabinet CSG uses the points raw.
 */
const FRONT_OPEN_INCHES = 4;
/**
 * Countertop hole vs the farmhouse BODY, per side, before Countertop.jsx's
 * own 1" inset. The bowl wall is 1" thick, so a 0 here puts the hole edge
 * exactly on the inner wall plane and the cabinet cavity shows through the
 * seam; -1/2" makes the slab overlap the wall by 1/2", an undermount reveal.
 */
const FARMHOUSE_COUNTER_OVERLAP_INCHES = -0.5;
function farmhouseProfile(dims, oversizeIn) {
  const halfW = dims.width / INCHES_TO_METERS / 2 + oversizeIn;
  const halfD = dims.depth / INCHES_TO_METERS / 2;
  const back = -halfD - oversizeIn;
  const front = halfD + FRONT_OPEN_INCHES;
  return {
    points: [
      { x: -halfW, z: back },
      { x: halfW, z: back },
      { x: halfW, z: front },
      { x: -halfW, z: front },
    ],
    metadata: {
      bounds: { minX: -halfW, maxX: halfW, minZ: back, maxZ: front },
      openFront: true,
      originRelative: true,
    },
  };
}
function buildFarmhouseSinkCutout(dims) {
  return farmhouseProfile(dims, FARMHOUSE_COUNTER_OVERLAP_INCHES);
}
function buildFarmhouseSinkCabinetCutout(dims) {
  return farmhouseProfile(dims, CABINET_OVERSIZE_INCHES);
}

/** Cutout pair for either subtype, so call sites never branch twice. */
function buildKitchenSinkCutouts(subtype, dims) {
  return subtype === 'farmhouse'
    ? {
        'sink-cutout': buildFarmhouseSinkCutout(dims),
        'sink-cabinet-cutout': buildFarmhouseSinkCabinetCutout(dims),
      }
    : {
        'sink-cutout': buildKitchenSinkCutout(dims),
        'sink-cabinet-cutout': buildKitchenSinkCabinetCutout(dims),
      };
}

module.exports = {
  placeholderBasinFor,
  buildKitchenSinkCutout,
  buildKitchenSinkCabinetCutout,
  buildFarmhouseSinkCutout,
  buildFarmhouseSinkCabinetCutout,
  buildKitchenSinkCutouts,
  buildKitchenSinkDimensions,
};
